import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { fbt } from 'fbtee'
import { useAuth } from '../../hooks/useAuth'
import { useGroups } from '../../hooks/useGroups'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useEventSuggestions, type SuggestEventSource } from '../../hooks/useEventSuggestions'
import { useSearchIndex } from '../../hooks/useSearchIndex'
import { useTabReselect } from '../../hooks/useTabReselect'
import { suggestEventPrompt } from '../../lib/guestPrompts'
import { suggestEventLabel } from '../../lib/commonStrings'
import { usePageLoadTrace } from '../../lib/perf'
import { placeholders } from '../../lib/skeletonUtils'
import { track } from '../../lib/analytics'
import { PageLayout } from '../../components/PageLayout'
import { SuggestRow } from '../../components/SuggestRow'
import { SuggestIconButton } from '../../components/SuggestIconButton'
import { SuggestEventDialog } from '../../components/SuggestEventDialog'
import { SearchField } from '../../components/GlobalSearch/SearchField'
import { EventResultRow } from '../../components/GlobalSearch/SearchResultRows'
import { searchStrings } from '../../components/GlobalSearch/strings'
import { useAtTop } from '../../hooks/useAtTop'
import { navPillClass } from '../../lib/glass'
import { cn } from '../../lib/utils'
import { useSearchQuery } from './useSearchQuery'

// Once the page scrolls, the field and "+" turn into floating glass pills like the
// header's. dark: overrides because the input and outline button set dark: surfaces.
const PILL_CLASS = 'rounded-full transition-[background-color,border-color,box-shadow,backdrop-filter] duration-[400ms] ease-[cubic-bezier(0.25,0.8,0.25,1)] motion-reduce:transition-none'
const FLOATING_CLASS = cn(navPillClass, 'dark:border-border/60 dark:bg-card/55')

const GlobalSearchResults = lazy(() => import('../../components/GlobalSearch/GlobalSearchResults'))

export default function SearchPage() {
  useDocumentTitle(pageTitle(fbt('Search', 'Browser tab title for the Search tab')))
  const location = useLocation()
  const { signedInUid } = useAuth()
  const { groups } = useGroups(signedInUid ?? null)
  const { suggestEvent } = useEventSuggestions(signedInUid ?? null)
  const { guard } = useGuestGuard()
  const { loading: indexLoading } = useSearchIndex()
  const { query, setQuery } = useSearchQuery()
  const searchRef = useRef<HTMLInputElement>(null)
  const atTop = useAtTop()
  const [suggestSource, setSuggestSource] = useState<SuggestEventSource | null>(null)
  const openSuggest = guard((source: SuggestEventSource) => setSuggestSource(source), suggestEventPrompt())

  useEffect(() => {
    track('search_opened', { entry_point: 'tab' })
    // Once per visit to the tab.
  }, [location.key])

  // Opening the tab doesn't focus the field (#538); tapping the tab again does.
  useTabReselect('/search', () => searchRef.current?.focus())

  usePageLoadTrace('search_page_to_content', !indexLoading)

  return (
    <PageLayout hideOnMobile>
      {/* -mt-4/pt-4 mirror PageLayout's top padding, so the box doesn't jump
          when it starts sticking. At the top it bleeds to the edge to cover the
          results; scrolled, the bar goes clear and click-through around its pills. */}
      <div
        className={cn(
          'sticky top-[max(var(--app-header-h,0px),env(safe-area-inset-top))] z-40 -mx-4 -mt-4 px-4 pt-4 pb-2',
          atTop ? 'bg-background' : 'chrome-passthrough',
        )}
      >
        {/* The "+" renders for guests too so they always have an entry point —
            the guard handles the upsell. */}
        <div className="flex items-center gap-2">
          <SearchField
            ref={searchRef}
            value={query}
            onValueChange={setQuery}
            placeholder={searchStrings().placeholder}
            className={cn(PILL_CLASS, !atTop && FLOATING_CLASS)}
          />
          <SuggestIconButton
            className={cn('size-9', PILL_CLASS, !atTop && FLOATING_CLASS)}
            label={suggestEventLabel()}
            onClick={() => openSuggest('search_button')}
          />
        </div>
      </div>
      <Suspense fallback={placeholders(3).map((_, i) => <EventResultRow key={i} loading />)}>
        <GlobalSearchResults
          query={query}
          footer={<SuggestRow onClick={() => openSuggest('search_empty')} label={suggestEventLabel()} />}
        />
      </Suspense>
      <SuggestEventDialog
        open={suggestSource !== null}
        onOpenChange={next => !next && setSuggestSource(null)}
        groups={groups}
        source={suggestSource ?? 'search_empty'}
        onSubmit={suggestEvent}
      />
    </PageLayout>
  )
}
